import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Wraps the header search dialog's two alternate views — the normal predictive search form, and
 * the barcode-scanner camera view opened from the search bar's barcode icon (see
 * snippets/search.liquid) — and switches between them.
 *
 * A scan that doesn't end in a redirect (camera unavailable, no match, lookup error, ...) reverts
 * to the normal search view with a small inline notice — see the BarcodeScannerUnresolvedEvent
 * listener below (assets/barcode-scanner.js dispatches it).
 *
 * @extends {Component}
 */
class SearchModalBodyComponent extends Component {
  /** @type {HTMLElement | null} */
  #predictiveSearch = null;

  /** @type {(HTMLElement & { reset(): void }) | null} */
  #barcodeScanner = null;

  /** @type {HTMLElement | null} */
  #barcodeNotice = null;

  /** @type {HTMLElement[]} */
  #barcodeModeButtons = [];

  /** @type {HTMLElement[]} */
  #searchModeButtons = [];

  /** @type {ReturnType<typeof this.closest> } */
  #dialog = null;

  connectedCallback() {
    super.connectedCallback();

    this.#predictiveSearch = this.querySelector('predictive-search-component');
    this.#barcodeScanner = this.querySelector('barcode-scanner-component');
    this.#barcodeNotice = this.querySelector('.predictive-search__barcode-notice');
    // Both views (predictive-search-component and barcode-scanner-view.liquid) render their own
    // instance of the barcode/search switch, kept in the same on-screen position across views —
    // see .barcode-scanner__mode-switch in snippets/barcode-scanner-view.liquid — so every one of
    // them needs to be kept in sync, not just the first match in DOM order.
    this.#barcodeModeButtons = Array.from(this.querySelectorAll('.predictive-search__barcode-link'));
    this.#searchModeButtons = Array.from(this.querySelectorAll('.predictive-search__search-mode-button'));
    this.#dialog = this.closest('dialog-component');

    this.addEventListener(ThemeEvents.barcodeScannerUnresolved, this.#handleUnresolved);
    this.#dialog?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.barcodeScannerUnresolved, this.#handleUnresolved);
    this.#dialog?.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  showBarcodeScanner() {
    /** @type {any} */ (this.#dialog)?.showDialog();

    if (!this.#barcodeScanner || !this.#predictiveSearch) return;

    if (this.#barcodeNotice) this.#setVisible(this.#barcodeNotice, false);
    this.#setVisible(this.#predictiveSearch, false);
    this.#setVisible(this.#barcodeScanner, true);
    this.#setMode(true);

    // dialog-component's own showDialog() defers the actual showModal() call to a
    // requestAnimationFrame, and *that* is what runs the dialog's native focusing steps — since
    // the search input carries `autofocus` (see snippets/search-modal.liquid) but is now hidden,
    // those steps would fall back to focusing the <dialog> itself. Deferring this focus call the
    // same way queues it to run in that same animation frame but after showModal(), so it's the
    // one that wins instead of being immediately overridden by the native fallback.
    requestAnimationFrame(() => {
      this.#barcodeScanner?.querySelector('.barcode-scanner__actions button')?.focus();
    });
  }

  /**
   * Switches back to the normal search view. Called both by a shopper's own "search by product
   * name instead" choice inside the barcode view (see snippets/barcode-scanner-view.liquid) and,
   * with a notice attached, whenever a scan doesn't resolve (see #handleUnresolved below).
   */
  showSearch() {
    this.#showSearch();
  }

  /**
   * @param {string} [noticeText]
   * @param {boolean} [focusInput] - False when called on dialog close, where the dialog (and
   *   everything in it) is no longer visible, so moving focus into it would be pointless at
   *   best and could steal focus from whatever the shopper interacted with instead.
   */
  #showSearch(noticeText, focusInput = true) {
    if (!this.#barcodeScanner || !this.#predictiveSearch) return;

    this.#barcodeScanner.reset();
    this.#setVisible(this.#barcodeScanner, false);
    this.#setVisible(this.#predictiveSearch, true);
    this.#setMode(false);

    if (this.#barcodeNotice) {
      const text = this.#barcodeNotice.querySelector('.predictive-search__barcode-notice-text');
      if (text) text.textContent = noticeText ?? '';
      this.#setVisible(this.#barcodeNotice, Boolean(noticeText));
    }

    if (focusInput) this.#predictiveSearch.querySelector('.search-input')?.focus();
  }

  /**
   * Sets both the `hidden` property (for semantics/assistive tech) and an inline `display`
   * override (so the visual result doesn't depend on winning a specificity fight against
   * predictive-search-component's/barcode-scanner-component's own `display: flex` rules —
   * inline styles always win over any stylesheet rule).
   * @param {HTMLElement} element
   * @param {boolean} visible
   */
  #setVisible(element, visible) {
    element.hidden = !visible;
    element.style.display = visible ? '' : 'none';
  }

  /**
   * Reflects which view is showing on every barcode/search mode switch in the dialog (see
   * .predictive-search__mode-switch in snippets/search-modal.liquid and
   * snippets/barcode-scanner-view.liquid) — CSS keys off aria-pressed via :has() to swap which
   * button carries the switch's solid accent color.
   * @param {boolean} isBarcode
   */
  #setMode(isBarcode) {
    for (const button of this.#barcodeModeButtons) button.setAttribute('aria-pressed', String(isBarcode));
    for (const button of this.#searchModeButtons) button.setAttribute('aria-pressed', String(!isBarcode));
  }

  /** @param {Event} event */
  #handleUnresolved = (event) => {
    const reason = /** @type {any} */ (event).detail?.reason;
    const messagesByReason = /** @type {Record<string, string | undefined>} */ ({
      camera_unavailable: this.dataset.msgCameraUnavailable,
      scanning_unavailable: this.dataset.msgScanningUnavailable,
      not_found: this.dataset.msgNotFound,
      lookup_error: this.dataset.msgLookupError,
    });

    this.#showSearch(messagesByReason[reason]);
  };

  #handleDialogClose = () => {
    this.#showSearch(undefined, false);
  };
}

if (!customElements.get('search-modal-body-component')) {
  customElements.define('search-modal-body-component', SearchModalBodyComponent);
}
